import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  primary_color: "#2563EB",
  accent_color: "#16A34A",
  email_signature: null,
  logo_url: null,
  logo_file_id: null,
};

const BrandingContext = createContext({ branding: DEFAULTS, refresh: () => {} });

/**
 * Fetches the caller's company branding once on mount, injects two CSS
 * variables on :root (`--brand-primary`, `--brand-accent`) so any styled
 * element can pick up the tenant theme via Tailwind arbitrary values like
 * `bg-[var(--brand-primary)]`. The public marketing site does not mount
 * this provider so it always keeps the NextRota palette.
 */
export function BrandingProvider({ enabled, children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      setBranding({ ...DEFAULTS, ...data });
    } catch {
      setBranding(DEFAULTS);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setBranding(DEFAULTS);
      return;
    }
    refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", branding.primary_color || DEFAULTS.primary_color);
    root.style.setProperty("--brand-accent",  branding.accent_color  || DEFAULTS.accent_color);
  }, [branding.primary_color, branding.accent_color]);

  return (
    <BrandingContext.Provider value={{ branding, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
